import { useApiQuery, type Api } from "../../../services/api";
import { useActiveLang } from "../../app/hooks/useActiveLang";

/**
 * One journal of the practice's current view, INFERRED from the router. `uid` is
 * what travels back as a filter; `name` is the venue's own name, which no locale
 * translates.
 */
export type PracticeJournalValue =
  Api["get"]["/webapp/private/practice-profiles/:uid/filter-values"]["output"]["body"]["journals"][number];

/**
 * One publication type, as the CANONICAL taxonomy string.
 *
 * It arrives with no label on purpose. `getArticleTypeText` already owns the
 * display name for all 26 canonical types, in all eight locales, with the
 * abbreviation each locale actually uses ("RCT" in EN, "ECR" in FR, spelled out
 * in AR because a Latin acronym clashes with the script) — so the drawer renders
 * these exactly as every article card in the app already does. A label served
 * from the API would be a second copy, in the wrong language.
 */
export type PracticePublicationTypeValue =
  Api["get"]["/webapp/private/practice-profiles/:uid/filter-values"]["output"]["body"]["publicationTypes"][number];

/**
 * A DISCRIMINATED UNION, for the reason its two neighbours are: the three
 * answers are not the same answer.
 *
 * `ready` with two empty lists is NOT an error and must never collapse into one.
 * It means the practice's current view is made of nothing worth naming — a thin
 * corpus, or a rail chip narrow enough to leave one paper — and the drawer says
 * so per axis rather than pretending it failed.
 */
export type PracticeFilterValuesData =
  | { status: "loading" }
  | {
      status: "ready";
      journals: ReadonlyArray<PracticeJournalValue>;
      publicationTypes: ReadonlyArray<PracticePublicationTypeValue>;
    }
  | { status: "unsupported" };

/**
 * The journals and publication types behind the drawer's two axes, for ONE
 * practice narrowed by the rail's live chip.
 *
 * ── Fired on the drawer OPENING, not with the page ──────────────────────────
 * `null` is what a closed drawer passes, and it is the whole reason this is a
 * parameter rather than a hook that always runs. Answering it costs another
 * evaluation of the practice's matched set, which is the expensive half of this
 * feature, and most readers never open the panel. ONE parameter and not a
 * practice plus an `enabled` flag, because the two could never legitimately
 * disagree.
 *
 * ── Why the counts do not move when a box is ticked ─────────────────────────
 * `descriptorUi` is the ONLY selection that scopes them. Recomputing each axis
 * against the other's checkboxes is what a full facet search does, and it costs
 * one matched-set evaluation per axis plus a refetch on every tap. Here a count
 * means "what this value ALONE would open", which is checkable in one tap and
 * holds as an exact equality against the feed's total. With two axes on, the
 * numbers on screen are upper bounds.
 *
 * Every failure reads as `unsupported`, exactly as the rail's does and for the
 * same reason: the drawer, the rail and the feed are three halves of one compiled
 * Boolean, and this panel is only reachable from a rail that is already answering.
 */
export function usePracticeFilterValues(
  request: { practiceUid: string; descriptorUi: string | null } | null
): PracticeFilterValuesData {
  const { contentLang } = useActiveLang();

  const query = useApiQuery(
    "/webapp/private/practice-profiles/:uid/filter-values",
    request === null
      ? false
      : {
          param: { uid: request.practiceUid },
          // Two explicit shapes rather than one carrying `descriptorUi:
          // undefined`: the parameter is absent from the request or it is a
          // value, and a narrowed drawer is a different answer rather than the
          // same one re-read.
          query:
            request.descriptorUi === null
              ? { language: contentLang }
              : { language: contentLang, descriptorUi: request.descriptorUi },
          // Matches the endpoint's own `private, max-age=300`. It is what stops a
          // reader who opens, closes and reopens the panel from re-evaluating the
          // Boolean each time.
          staleTime: 1000 * 60 * 5,
        }
  );

  switch (query.state) {
    // A closed drawer. The caller that switched the query off is the same one not
    // rendering its result, so the honest report is the one it already ignores.
    case "idle":
      return { status: "ready", journals: [], publicationTypes: [] };
    case "pending":
      return { status: "loading" };
    case "error":
      return { status: "unsupported" };
    case "success":
      return {
        status: "ready",
        journals: query.body.journals,
        publicationTypes: query.body.publicationTypes,
      };
    default:
      throw new Error(query satisfies never);
  }
}
